// Imports
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import theme from './shareables/theme';
import styled, { css } from 'styled-components/macro';
import { darken, transparentize } from 'polished';
import GlobalStyle from './shareables/foundation/simple-ui/components/global-style';
import Header from './shareables/foundation/simple-ui/components/header';
import Box from './shareables/foundation/simple-ui/components/box';
import Footer from './footer';
import { z } from 'zod';
import thirdPartyIdentifiers from './shareables/third-party-identifiers';
import Logo from './shareables/front-end/svg-logo';
import company from './shareables/company';
import isScreenshot from './shareables/utils/is-screenshot';


// Lazily-loaded components
const PasswordStrengthMeter = lazy(() => import('./password-strength-meter'));


// Styled components
const Notification = styled(Box)`
	padding: 1rem;
	margin-bottom: 1rem;
	color: #fff;
	line-height: 1.2;
`;

const SuccessNotification = styled(Notification)`
	background-color: ${theme.colors.success};
`;

const ErrorNotification = styled(Notification)`
	background-color: ${theme.colors.danger};
`;

const Field = styled.div`
	label {
		width: 100%;
		padding-bottom: 0.3em;
		font-weight: 600;
		display: block;
		color: #363636;
		line-height: 1;
		margin-bottom: 0.2rem;
		
		small {
			font-weight: 400;
			margin-left: 0.75em;
			font-style: italic;
			font-size: 75%;
		}
	}
	
	input {
		width: 100%;
		height: 2.25em;
		padding: 0 0.75em;
		appearance: none;
		background: #fff;
		font-size: 1em;
		font-family: inherit;
		transition: all 0.1s ease;
		border: 1px solid #000;
		border-color: #ddd;
		border-radius: 0.2em;
		line-height: 1.3;
		
		&:hover:not(:disabled) {
			border-color: ${darken(0.1, '#ddd')};
		}
		
		&:focus:not(:disabled) {
			border-color: ${theme.colors.primary};
			outline: none;
			box-shadow: 0 0 0 0.125em ${transparentize(0.75, theme.colors.primary)};
		}
		
		&:disabled {
			background: #eee;
		}
		
		&::placeholder {
			color: #999;
		}
	}
	
	&:not(:last-child) {
		margin-bottom: 1.25em;
	}
`;

const FlexWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	
	a {
		font-size: 0.9em;
		margin: auto 0;
	}
`;

interface SubmitProps {
	isLoading?: boolean;
}

const Submit = styled.button<SubmitProps>`
	appearance: none;
	border: 0;
	height: 2.25em;
	border-radius: 0.25rem;
	display: inline-flex;
	align-items: center;
	padding: 0 0.75em;
	white-space: nowrap;
	background: ${theme.colors.accent};
	color: #fff !important;
	font-size: 1em;
	transition: all 0.15s ease;
	position: relative;
	user-select: none;
	line-height: 1;
	text-decoration: none;
	margin-right: 1em;
	
	${(props) => (props.isLoading ? 'color: transparent !important; pointer-events: none;' : '')}
	
	&:hover:not(:disabled) {
		background: ${darken(0.05, theme.colors.accent)};
		cursor: pointer;
	}
	
	&:active:not(:disabled) {
		background: ${darken(0.1, theme.colors.accent)};
	}
	
	&:focus:not(:disabled),
	&:focus-within:not(:disabled) {
		outline: 0;
		box-shadow:
			0 0 0 0.125em #fff,
			0 0 0 0.25em ${transparentize(0.5, theme.colors.accent)};
	}
	
	&:disabled {
		opacity: 0.5;
		cursor: default;
		
		&:active {
			pointer-events: none;
		}
	}
	
	&::-moz-focus-inner {
		border: 0;
	}
	
	${(props) =>
		props.isLoading
			? css`
					&::after {
						border-color: transparent transparent #fff #fff !important;
						position: absolute;
						left: calc(50% - 0.5em - 2px);
						top: calc(50% - 0.5em - 2px);
						animation: spinAround 0.5s linear infinite;
						border-radius: 290486px;
						border-style: solid;
						border-width: 2px;
						content: '';
						display: block;
						height: 1em;
						width: 1em;
					}
				`
			: ''}
	
	@keyframes spinAround {
		from {
			transform: rotate(0deg);
		}
		
		to {
			transform: rotate(359deg);
		}
	}
`;

const LoadingIndicator = styled.div`
	&::after {
		border-color: transparent transparent #555 #555 !important;
		animation: spinAround 0.5s linear infinite;
		border-radius: 290486px;
		border-style: solid;
		border-width: 2px;
		content: '';
		display: block;
		height: 2em;
		width: 2em;
		margin: auto;
	}
	
	@keyframes spinAround {
		from {
			transform: rotate(0deg);
		}
		
		to {
			transform: rotate(359deg);
		}
	}
`;

const VisibleChallenge = styled.div`
	> div {
		margin-bottom: 1.25em;
	}
`;

const ConfirmationMessage = styled.p`
	margin-bottom: 1.25em;
`;


// Define the accepted props
interface Props {
	storybookCustomName?: string;
	storybookEmail?: string;
	storybookPassword?: string;
	storybookSubmitting?: boolean;
	storybookSuccessMessage?: string;
	storybookErrorMessage?: string;
	storybookRedirecting?: boolean;
	storybookPathname?: string;
	storybookForgotPasswordStep?: number;
}


// Function component
const App: React.FC<Props> = ({
	storybookCustomName,
	storybookEmail,
	storybookPassword,
	storybookSubmitting,
	storybookSuccessMessage,
	storybookErrorMessage,
	storybookRedirecting,
	storybookPathname,
	storybookForgotPasswordStep,
}) => {
	// Get URL params
	const params = new URLSearchParams(document.location.search.slice(1));
	
	
	// Use state
	const [email, setEmail] = useState(storybookEmail ?? '');
	const [password, setPassword] = useState(storybookPassword ?? '');
	const [remember, setRemember] = useState(true);
	const [passwordHidden, setPasswordHidden] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(storybookSubmitting ?? false);
	const [successMessage, setSuccessMessage] = useState<string | null>(
		storybookSuccessMessage ?? (params.get('signedOut') ? 'You’ve been signed out.' : undefined) ?? null
	);
	
	const [errorMessage, setErrorMessage] = useState<string | null>(storybookErrorMessage ?? null);
	const [isRedirecting, setIsRedirecting] = useState(storybookRedirecting ?? false);
	const [visibleChallengeActive, setVisibleChallengeActive] = useState(false);
	const [locationState, setLocationState] = useState({
		pathname: storybookPathname ?? window.location.pathname,
	});
	
	const [forgotPasswordStep, setForgotPasswordStep] = useState(storybookForgotPasswordStep ?? 0);
	const [verificationCode, setVerificationCode] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [returnURL, setReturnURL] = useState('');
	
	
	// Ensure we've visited `auth` in the staging environment
	//  -> This will ensure the user has passed through Cloudflare Access with `auth`
	useEffect(() => {
		if (process.env.REACT_APP__ENVIRONMENT !== 'staging') {
			return;
		}
		
		if ((window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			return;
		}
		
		if (!params.get('visited-auth')) {
			window.location.href = `https://auth.api.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/redirect${params.toString() ? `?${params.toString()}` : ''}`;
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Listen for browser history "popstate" events
	useEffect(() => {
		const handlePopState = () => {
			setLocationState({
				pathname: window.location.pathname,
			});
		};
		
		window.addEventListener('popstate', handlePopState);
		
		return () => window.removeEventListener('popstate', handlePopState);
	}, []);
	
	
	// Reset things when location changes
	//  -> Borrows from the design of `useUpdateOnlyEffect()`, which isn't available in the SSO front end
	const hasRun = useRef(false);
	
	useEffect(() => {
		// Run the effect only if it has already run once
		if (!hasRun.current) {
			hasRun.current = true;
			return;
		}
		
		
		// Reset various states
		setForgotPasswordStep(storybookForgotPasswordStep ?? 0);
		setPasswordHidden(true);
		setPassword(storybookPassword ?? '');
		setVerificationCode('');
		setNewPassword('');
		setReturnURL('');
		setSuccessMessage(storybookSuccessMessage ?? null);
		setErrorMessage(storybookErrorMessage ?? null);
		
		
		// On unmount, reset flag indicating the effect has run
		return () => {
			hasRun.current = false;
		};
	}, [locationState, storybookForgotPasswordStep, storybookPassword, storybookSuccessMessage, storybookErrorMessage]);
	
	
	// Update the page title
	useEffect(() => {
		if (!locationState.pathname.startsWith('/forgot')) {
			document.title = `Sign in // ${company.name}`;
		} else {
			document.title = `Forgot // ${company.name}`;
		}
	}, [locationState]);
	
	
	// Determine reCAPTCHA site keys
	const recaptchaSiteKeyInvisible =
		thirdPartyIdentifiers.recaptchaSiteKeys?.invisible[
			process.env.REACT_APP__ENVIRONMENT === 'production' ? 'prd' : 'dev'
		];
	
	const recaptchaSiteKeyVisible =
		thirdPartyIdentifiers.recaptchaSiteKeys?.visible[
			process.env.REACT_APP__ENVIRONMENT === 'production' ? 'prd' : 'dev'
		];
	
	
	// Handle submissions
	const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
		// Prevent the form's default submission behavior
		event?.preventDefault();
		
		
		// Verify reCAPTCHA is available
		if (typeof window.grecaptcha !== 'object') {
			// Set error message
			setErrorMessage('reCAPTCHA hasn’t loaded yet.');
			
			
			// Stop processing
			return;
		}
		
		
		// Set is submitting
		setIsSubmitting(true);
		
		
		// Get the correct reCAPTCHA headers
		const getRecaptchaHeaders = async () => {
			if (visibleChallengeActive) {
				return {
					'Recaptcha-Version': 'visible',
					'Recaptcha-Token': window.grecaptcha.enterprise.getResponse(),
				};
			}
			
			return {
				'Recaptcha-Version': 'invisible',
				'Recaptcha-Token': await window.grecaptcha.enterprise.execute(recaptchaSiteKeyInvisible, {
					action: 'submit',
				}),
			};
		};
		
		
		// Call reCAPTCHA
		void getRecaptchaHeaders().then((recaptchaHeaders) => {
			// Form fetch options
			const fetchOptions: RequestInit = {
				credentials: 'include',
				mode: 'cors',
				cache: 'no-store',
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...recaptchaHeaders,
				},
				body:
					locationState.pathname !== '/forgot'
						? JSON.stringify({
								email: email,
								password: password,
								remember: remember,
								return: params.get('return') ?? `https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`,
							})
						: forgotPasswordStep === 0
							? JSON.stringify({
									email: email,
									return: params.get('return') ?? `https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`,
								})
							: forgotPasswordStep === 1
								? JSON.stringify({
										email: email,
										verificationCode: verificationCode,
										return: params.get('return') ?? `https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`,
									})
								: forgotPasswordStep === 2
									? JSON.stringify({
											email: email,
											verificationCode: verificationCode,
											newPassword: newPassword,
											return: params.get('return') ?? `https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`,
										})
									: '',
			};
			
			
			// Make the fetch
			fetch(
				`https://auth.api.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/${
					locationState.pathname !== '/forgot'
						? 'authenticate'
						: forgotPasswordStep === 0
							? 'initiate-reset'
							: forgotPasswordStep === 1
								? 'verify-email'
								: forgotPasswordStep === 2
									? 'reset-password'
									: ''
				}`,
				fetchOptions
			)
				.then((responseObject) => {
					// Reset back to not submitting
					setIsSubmitting(false);
					
					
					// Clear messages
					setSuccessMessage(null);
					setErrorMessage(null);
					
					
					// Get text from response
					responseObject
						.text()
						.then((response) => {
							// Reset reCAPTCHA if it's visible at this point
							if (visibleChallengeActive) {
								grecaptcha.enterprise.reset();
							}
							
							
							// Check if rate limited
							if (responseObject.status === 429) {
								// Set error message
								setErrorMessage(`You’ve made too many attempts. Please wait to try again.`);
								
								
								// Stop processing
								return;
							}
							
							
							// Check if reCAPTCHA visible challenge needs to be shown
							if (responseObject.status === 499) {
								// Render visible challenge, if not already
								if (!visibleChallengeActive) {
									grecaptcha.enterprise.render('recaptcha-visible-challenge', {
										sitekey: recaptchaSiteKeyVisible,
									});
									
									setVisibleChallengeActive(true);
								}
								
								
								// Stop processing
								return;
							}
							
							
							// Check for a problematic status code
							if (responseObject.status !== 200) {
								// Set error message
								setErrorMessage(`Unexpected status code: ${responseObject.status}`);
								
								
								// Stop processing
								return;
							}
							
							
							// Attempt to parse JSON
							const authenticationResponseSchema = z.union([
								z.object({
									status: z.literal('success'),
									response: z.object({
										return: z.string(),
									}),
								}),
								z.object({
									status: z.literal('error'),
									message: z.string(),
								}),
							]);
							
							let json: z.infer<typeof authenticationResponseSchema>;
							
							try {
								json = authenticationResponseSchema.parse(JSON.parse(response));
							} catch (problem) {
								// Log problem
								console.error(problem);
								
								
								// Set error message
								setErrorMessage('An unexpected issue occurred while decoding the back end’s response.');
								
								
								// Stop processing
								return;
							}
							
							
							// Handle successful submission
							if (json.status === 'success') {
								if (locationState.pathname !== '/forgot') {
									// Update state
									setIsRedirecting(true);
									
									
									// Redirect
									window.location.href = json.response.return;
								} else if (forgotPasswordStep === 0) {
									setForgotPasswordStep(1);
								} else if (forgotPasswordStep === 1) {
									setForgotPasswordStep(2);
								} else if (forgotPasswordStep === 2) {
									setReturnURL(json.response.return);
									setForgotPasswordStep(3);
								} else {
									// Set error message
									setErrorMessage('An unexpected issue occurred while handling the reset process.');
								}
							} else {
								// Set error message
								setErrorMessage(`${json.message}`);
							}
						})
						.catch((problem) => {
							// Log problem
							console.error(problem);
							
							
							// Set error message
							setErrorMessage('An unexpected issue occurred while retrieving a response from the back end.');
						});
				})
				.catch((problem) => {
					// Log problem
					console.error(problem);
					
					
					// Reset back to not submitting
					setIsSubmitting(false);
					
					
					// Set error message
					setErrorMessage(`The attempt failed, likely due to making too many attempts. Please wait to try again.`);
				});
		});
	};
	
	
	// Set favicon
	useEffect(() => {
		if (isScreenshot()) {
			return;
		}
		
		const link = document.createElement('link');
		link.rel = 'shortcut icon';
		link.href = `https://cdn.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/images/favicons/sign-in.png`;
		document.head.appendChild(link);
	}, []);
	
	
	// Load reCAPTCHA script
	useEffect(() => {
		if (isScreenshot()) {
			return;
		}
		
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKeyInvisible}`;
		
		script.async = true;
		document.head.appendChild(script);
		
		return () => {
			document.head.removeChild(script);
		};
	}, [recaptchaSiteKeyInvisible]);
	
	
	// Build search params for navigation
	const customName = new URLSearchParams(document.location.search).get('custom-name');
	
	const navigationSearchParams = new URLSearchParams();
	navigationSearchParams.append(
		'return',
		params.get('return') ?? `https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`
	);
	
	if (customName) {
		navigationSearchParams.append('custom-name', customName);
	}
	
	
	// Create a reusable "back to sign in" link
	const backToSignInLink = (
		<a
			href={`/?${navigationSearchParams.toString()}`}
			onClick={(event) => {
				// Ignore the navigation when clicked using right mouse button or by holding a special modifier key
				if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.button !== 0) {
					return;
				}
				
				
				// Redirect, if needed
				if (!event.defaultPrevented) {
					// Prevent default behavior
					event.preventDefault();
					
					
					// Navigate
					window.history.pushState(undefined, '', `/?${navigationSearchParams.toString()}`);
					
					setLocationState({
						pathname: '/',
					});
				}
			}}
		>
			Back to sign in
		</a>
	);
	
	
	// Return JSX
	return (
		<React.StrictMode>
			<GlobalStyle />
			
			<Header
				storybookCustomName={storybookCustomName}
				logoElement={
					(window as typeof window & { STORYBOOK?: true }).STORYBOOK ? (
						<img src='/images/placeholders/400x100.png' alt='Placeholder logo' />
					) : (
						<Logo />
					)
				}
				title={locationState.pathname !== '/forgot' ? 'Sign in' : 'Forgot'}
			/>
			
			{successMessage && <SuccessNotification>{successMessage}</SuccessNotification>}
			
			{errorMessage && (
				<ErrorNotification>
					<strong>Oh snap!</strong> {errorMessage}
				</ErrorNotification>
			)}
			
			<Box as='main'>
				{!isRedirecting ? (
					<React.Fragment>
						{locationState.pathname !== '/forgot' ? (
							<form onSubmit={handleSubmit} key='authenticate'>
								<Field>
									<label htmlFor='email'>Email address</label>
									<input
										type='email'
										id='email'
										required
										value={email}
										onChange={(event) => setEmail(event.currentTarget.value)}
										spellCheck='false'
										autoCorrect='off'
										autoFocus
									/>
								</Field>
								
								<Field>
									<label htmlFor='password'>
										Password
										<small
											style={{
												textDecoration: 'underline',
												cursor: 'pointer',
												outline: 'none',
											}}
											onClick={() => setPasswordHidden(!passwordHidden)}
											onKeyDown={() => {
												// Do nothing, not focusable
											}}
											role='button'
											tabIndex={-1}
										>
											{passwordHidden ? 'Reveal' : 'Hide'}
										</small>
									</label>
									<input
										type={passwordHidden ? 'password' : 'text'}
										id='password'
										required
										spellCheck='false'
										autoCorrect='off'
										value={password}
										onChange={(event) => setPassword(event.currentTarget.value)}
									/>
								</Field>
								
								<label htmlFor='remember' style={{ display: 'block', marginBottom: '1.25em' }}>
									<input
										type='checkbox'
										id='remember'
										checked={remember}
										onChange={(event) => setRemember(event.currentTarget.checked)}
									/>{' '}
									Remember me
								</label>
								
								<VisibleChallenge id='recaptcha-visible-challenge' />
								
								<FlexWrapper>
									<Submit type='submit' isLoading={isSubmitting}>
										Sign in
									</Submit>
									
									<a
										href={`/forgot?${navigationSearchParams.toString()}`}
										onClick={(event) => {
											// Ignore the navigation when clicked using right mouse button or by holding a special modifier key
											if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.button !== 0) {
												return;
											}
											
											
											// Redirect, if needed
											if (!event.defaultPrevented) {
												// Prevent default behavior
												event.preventDefault();
												
												
												// Navigate
												window.history.pushState(undefined, '', `/forgot?${navigationSearchParams.toString()}`);
												
												setLocationState({
													pathname: '/forgot',
												});
											}
										}}
									>
										Forgot password?
									</a>
								</FlexWrapper>
							</form>
						) : forgotPasswordStep === 0 ? (
							<form onSubmit={handleSubmit} key='initiate-reset'>
								<Field>
									<label htmlFor='email'>Email address</label>
									<input
										type='email'
										id='email'
										required
										value={email}
										onChange={(event) => setEmail(event.currentTarget.value)}
										spellCheck='false'
										autoCorrect='off'
										autoFocus
									/>
								</Field>
								
								<VisibleChallenge id='recaptcha-visible-challenge' />
								
								<FlexWrapper>
									<Submit type='submit' isLoading={isSubmitting}>
										Send verification email
									</Submit>
									
									{backToSignInLink}
								</FlexWrapper>
							</form>
						) : forgotPasswordStep === 1 ? (
							<form onSubmit={handleSubmit} key='verify-email'>
								<Field>
									<label htmlFor='verificationCode'>Verify your email address</label>
									<input
										type='text'
										id='verificationCode'
										inputMode='numeric'
										spellCheck='false'
										autoCorrect='off'
										required
										value={verificationCode}
										onChange={(event) => setVerificationCode(event.currentTarget.value)}
										placeholder='6-digit code'
										minLength={6}
										maxLength={6}
										pattern='[0-9]*'
										title='Must be a 6-digit code'
										autoFocus
										autoComplete='off'
									/>
								</Field>
								
								<VisibleChallenge id='recaptcha-visible-challenge' />
								
								<FlexWrapper>
									<Submit type='submit' isLoading={isSubmitting}>
										Continue
									</Submit>
									
									{backToSignInLink}
								</FlexWrapper>
							</form>
						) : forgotPasswordStep === 2 ? (
							<form onSubmit={handleSubmit} key='reset-password'>
								<Field>
									<label htmlFor='newPassword'>
										New password
										<small
											style={{
												textDecoration: 'underline',
												cursor: 'pointer',
												outline: 'none',
											}}
											onClick={() => setPasswordHidden(!passwordHidden)}
											onKeyDown={() => {
												// Do nothing, not focusable
											}}
											role='button'
											tabIndex={-1}
										>
											{passwordHidden ? 'Reveal' : 'Hide'}
										</small>
									</label>
									
									<input
										type={passwordHidden ? 'password' : 'text'}
										id='newPassword'
										value={newPassword}
										onChange={(event) => setNewPassword(event.currentTarget.value)}
										required
										autoFocus
										spellCheck='false'
										autoCorrect='off'
									/>
									
									<Suspense fallback={<React.Fragment></React.Fragment>}>
										<PasswordStrengthMeter password={newPassword} />
									</Suspense>
								</Field>
								
								<VisibleChallenge id='recaptcha-visible-challenge' />
								
								<FlexWrapper>
									<Submit type='submit' isLoading={isSubmitting}>
										Reset password
									</Submit>
									
									{backToSignInLink}
								</FlexWrapper>
							</form>
						) : forgotPasswordStep === 3 ? (
							<React.Fragment>
								<ConfirmationMessage>You’ve successfully reset your password!</ConfirmationMessage>
								
								<Submit as='a' href={returnURL}>
									Continue
								</Submit>
							</React.Fragment>
						) : (
							<React.Fragment>Unexpected error.</React.Fragment>
						)}
					</React.Fragment>
				) : (
					<React.Fragment>
						<LoadingIndicator />
						<div style={{ textAlign: 'center', marginTop: '1em' }}>Redirecting…</div>
					</React.Fragment>
				)}
			</Box>
			
			<Footer />
		</React.StrictMode>
	);
};

export default App;
